import { handleResponseErrors } from '@restDataAccess/handleResponseErrors';
import { findIdInUrl, getValidId, type WithId } from '@restDataAccess/withId';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';

import type { ApiNameType } from '@/constants';

import { authorizationHeader } from '../../authorizationHeader.ts';
import { useClearCacheCallback } from '../cacheHooks.ts';
import { assertApiBaseForFetch } from '../getApiUrl/getApiUrl.functions.ts';
import { useGetApiUrl } from '../useGetApiUrl.ts';

type AuthApiUpdateById = {
  baseApiRoute: ApiNameType;
  cacheKeys: string[];
  headers?: HeadersInit;
  identifier?: string;
  putOrPatch?: 'PUT' | 'PATCH';
  route: string;
};

type DeviceWithId<T> = Partial<T> & WithId & { name?: string };

export const useAuthApiUpdateManyDevicesById = <T extends WithId, V = void>({
  baseApiRoute,
  cacheKeys,
  headers,
  identifier,
  putOrPatch = 'PUT',
  route,
}: AuthApiUpdateById) => {
  const auth = useAuth();
  const api = useGetApiUrl(baseApiRoute);
  const clearCacheCallback = useClearCacheCallback(cacheKeys);

  const updateDevices = async (devices: DeviceWithId<T>[]) => {
    const successfulUpdates: V[] = [];
    const failedUpdates: { device: DeviceWithId<T>; error: { message: string } }[] = [];

    try {
      const base = assertApiBaseForFetch(api);
      for (const device of devices) {
        const deviceName = identifier ?? device.name;
        const id = getValidId(device);
        const idNameToReplace = findIdInUrl(route);
        if (!idNameToReplace) {
          throw new Error(`No id name found in route ${route}`);
        }

        try {
          const response = await fetch(`${base}${route.replace(`{${idNameToReplace}}`, id)}`, {
            method: putOrPatch,
            body: JSON.stringify(device),
            headers: {
              ...authorizationHeader(auth.user),
              'Content-Type': 'application/json',
              ...headers,
            },
          });

          const result: V = await handleResponseErrors<V>(response, deviceName);
          successfulUpdates.push(result);
        } catch (err) {
          failedUpdates.push({ device, error: err as { message: string } });
          console.error(`Failed to update device ${deviceName}`, err);
        }
      }

      if (failedUpdates.length > 0) {
        const errorMessages = `Some devices failed to update:\n${failedUpdates
          .map(f => f.error?.message || f.error)
          .join('\n')}`;
        throw new Error(errorMessages);
      }

      return successfulUpdates;
    } catch (error) {
      console.error(`Failed to update devices ${identifier}`, error);
      throw error;
    }
  };

  return useMutation({
    mutationFn: updateDevices,
    onSuccess: () => clearCacheCallback(),
    onError: () => clearCacheCallback(),
  });
};
